import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { SearchParam } from '@backend/types'
import {
  Scene,
  SceneResponse,
  ScenesResponse,
  SceneCreate,
  SceneUpdate,
  FullSceneResponse,
  SceneUserResponse,
} from '@backend/scenes/scenesRouter'
import { SortOrdersBody } from '@backend/types'
import { IId, ISchemaAndId, ISchemaId } from '../../types/common'
import { prepareHeaders, handleApiError } from './helpers'

interface ISearchParams extends SearchParam, ISchemaId {}

export interface ISceneObject extends Scene {}

export interface IScene extends SceneResponse {}

export interface IFullScene extends FullSceneResponse {}

export interface ISceneCreate extends SceneCreate {}

export interface ISceneUpdate extends SceneUpdate, IId {}

export interface IUser extends SceneUserResponse {}

export const scenesApi = createApi({
  reducerPath: 'scenes',
  baseQuery: fetchBaseQuery({
    baseUrl: window.env.API_ENDPOINT,
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    getScenes: builder.query<ScenesResponse, ISearchParams>({
      query: ({ schemaId, limit, offset, sort, sortKey, key, value }) => ({
        url: `scenes/schema/${schemaId}`,
        params: { limit, offset, sort, sortKey, key, value },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.getScenes', err)
        }
      },
    }),
    addScene: builder.query<void, ISceneCreate & ISchemaId>({
      query: ({ schemaId, name }) => ({
        url: `scenes/schema/${schemaId}`,
        method: 'POST',
        body: {
          name: name.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.addScene', err)
        }
      },
    }),
    getScene: builder.query<IFullScene, ISchemaId & IId>({
      query: ({ schemaId, id }) => ({
        url: `scenes/schema/${schemaId}/${id}`,
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.getScene', err)
        }
      },
    }),
    updateScene: builder.query<void, ISceneUpdate & ISchemaId>({
      query: ({ schemaId, id, name, userPriorities }) => ({
        url: `scenes/schema/${schemaId}/${id}`,
        method: 'PATCH',
        body: {
          name: name?.trim(),
          userPriorities,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.updateScene', err)
        }
      },
    }),
    deleteScene: builder.query<void, ISchemaAndId>({
      query: ({ schemaId, id }) => ({
        url: `scenes/schema/${schemaId}/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.deleteScene', err)
        }
      },
    }),
    addUsersToScen: builder.query<void, ISchemaAndId & { userIds: number[] }>({
      query: ({ schemaId, id, userIds }) => ({
        url: `scenes/schema/${schemaId}/${id}/users`,
        method: 'POST',
        body: {
          ids: userIds,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.addUsersToScen', err)
        }
      },
    }),
    removeUserFromScen: builder.query<void, IId & { userId: number }>({
      query: ({ id, userId }) => ({
        url: `scenes/scene/${id}/users/${userId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.removeUserFromScen', err)
        }
      },
    }),
    updateSortOrder: builder.query<void, SortOrdersBody & ISchemaId>({
      query: ({ schemaId, sortIds }) => ({
        url: `scenes/schema/${schemaId}/sort-order`,
        method: 'PATCH',
        body: {
          sortIds,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.scenes.errors.updateSortOrder', err)
        }
      },
    }),
  }),
})

export const {
  useLazyGetScenesQuery,
  useLazyAddSceneQuery,
  useLazyGetSceneQuery,
  useLazyUpdateSceneQuery,
  useLazyDeleteSceneQuery,
  useLazyAddUsersToScenQuery,
  useLazyRemoveUserFromScenQuery,
  useLazyUpdateSortOrderQuery,
} = scenesApi
